import { useState, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { api, fehlertext } from "./api";
import { Fehler, Karte, Knopf, Seitenkopf } from "./ui";

/**
 * Uebernahme der Kunden und Objekte aus der Altanwendung.
 *
 * Der Ablauf hat bewusst zwei Schritte. Zuerst der Probelauf: die Datei wird
 * gelesen, der Server rechnet durch, was entstuende, und schreibt nichts. Erst
 * danach erscheint der Knopf, der es wirklich tut.
 *
 * Der Grund ist nicht Vorsicht um ihrer selbst willen. Eine Uebernahme laeuft
 * einmal, und wenn sie schiefgeht, sitzt jemand einen Abend lang an sechzig
 * Karteikarten. Ein Blick vorher kostet zwei Minuten.
 */

interface Zeile {
  legacyRef: string;
  kunde: string;
  objekt: string;
  zustand: "neu" | "aktualisiert" | "uebersprungen";
  hinweise: string[];
}

interface Bericht {
  gelesen: number;
  neu: number;
  aktualisiert: number;
  uebersprungen: number;
  geschrieben: boolean;
  zeilen: Zeile[];
}

const DOKUMENT = "immobiliencheck-customer-db-v1";

export function UebernahmeSeite() {
  const { t } = useTranslation();
  const [kunden, setKunden] = useState<unknown[] | null>(null);
  const [dateiname, setDateiname] = useState("");
  const [bericht, setBericht] = useState<Bericht | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  async function dateiGewaehlt(e: ChangeEvent<HTMLInputElement>) {
    const datei = e.target.files?.[0];
    e.target.value = "";
    if (!datei) return;
    setFehler(null);
    setBericht(null);
    setKunden(null);
    try {
      const roh = JSON.parse(await datei.text());
      /*
       * Die Datei aus der Altanwendung enthaelt elf Dokumente. Gebraucht wird
       * eines davon. Der Rest bleibt hier liegen, statt ueber die Leitung zu
       * gehen: es waere ein Megabyte, das niemand liest.
       */
      const liste =
        roh?.documents?.[DOKUMENT]?.payload ??
        roh?.[DOKUMENT]?.payload ??
        (Array.isArray(roh) ? roh : null);
      if (!Array.isArray(liste)) {
        setFehler(t("uebernahme.falscheDatei"));
        return;
      }
      setKunden(liste);
      setDateiname(datei.name);
    } catch {
      setFehler(t("uebernahme.falscheDatei"));
    }
  }

  async function lauf(schreiben: boolean) {
    if (!kunden) return;
    setLaeuft(true);
    setFehler(null);
    try {
      setBericht(await api.post<Bericht>("/import/altanwendung", { kunden, schreiben }));
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <>
      <Seitenkopf titel={t("uebernahme.titel")} hinweis={t("uebernahme.hinweis")} />
      <Fehler text={fehler} />

      <Karte>
        <label className="cursor-pointer font-semibold text-verweis underline">
          {t("uebernahme.dateiWaehlen")}
          <input type="file" accept="application/json,.json" className="hidden" onChange={dateiGewaehlt} />
        </label>
        {kunden && (
          <p className="mt-2 text-sm text-leise">
            {t("uebernahme.gelesen", { datei: dateiname, count: kunden.length })}
          </p>
        )}
        {kunden && (
          <div className="mt-4 flex flex-wrap gap-2">
            <Knopf art="neben" disabled={laeuft} onClick={() => void lauf(false)}>
              {t("uebernahme.probe")}
            </Knopf>
            {bericht && !bericht.geschrieben && (
              <Knopf disabled={laeuft} onClick={() => void lauf(true)}>
                {t("uebernahme.uebernehmen")}
              </Knopf>
            )}
          </div>
        )}
      </Karte>

      {bericht && (
        <>
          <Karte className="mt-4">
            <p className="font-semibold">
              {bericht.geschrieben ? t("uebernahme.fertig") : t("uebernahme.probeErgebnis")}
            </p>
            <p className="mt-1 text-sm text-leise">
              {t("uebernahme.zusammenfassung", {
                gelesen: bericht.gelesen,
                neu: bericht.neu,
                aktualisiert: bericht.aktualisiert,
                uebersprungen: bericht.uebersprungen
              })}
            </p>
          </Karte>

          <Karte className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-leise">
                  <th className="pb-2 pe-4">{t("uebernahme.spalteKunde")}</th>
                  <th className="pb-2 pe-4">{t("uebernahme.spalteObjekt")}</th>
                  <th className="pb-2 pe-4">{t("uebernahme.spalteZustand")}</th>
                  <th className="pb-2">{t("uebernahme.spalteHinweise")}</th>
                </tr>
              </thead>
              <tbody>
                {bericht.zeilen.map((z) => (
                  <tr key={z.legacyRef} className="border-t border-linie align-top">
                    <td className="py-2 pe-4">{z.kunde}</td>
                    <td className="py-2 pe-4">{z.objekt}</td>
                    <td className="py-2 pe-4">
                      <span className={`rounded px-2 py-0.5 text-xs font-semibold ${
                        z.zustand === "neu" ? "bg-green-100 text-green-900"
                          : z.zustand === "aktualisiert" ? "bg-marke/30"
                          : "bg-red-100 text-red-800"
                      }`}>
                        {t(`uebernahme.zustand.${z.zustand}`)}
                      </span>
                    </td>
                    <td className="py-2 text-leise">{z.hinweise.join(" · ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Karte>
        </>
      )}
    </>
  );
}
